import { useMutation } from '@apollo/client';
import { useFragment } from '@apollo/client/react/hooks/useFragment';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { CandlestickChartIcon } from '@synoptic/ui-kit/icons/react/candlestick-chart.js';
import { CheckIcon } from '@synoptic/ui-kit/icons/react/check.tsx';
import { DownloadDataWindowIcon } from '@synoptic/ui-kit/icons/react/download-data-window.js';
import { PauseIcon } from '@synoptic/ui-kit/icons/react/pause.tsx';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { StarFilledIcon } from '@synoptic/ui-kit/icons/react/star-filled.js';
import { StarIcon } from '@synoptic/ui-kit/icons/react/star.js';
import { XmarkIcon } from '@synoptic/ui-kit/icons/react/xmark.tsx';
import { ModalTrigger } from '@synoptic/ui-kit/modal/modal.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { Fragment } from 'react/jsx-runtime';
import { gql } from '@/__generated__';
import { StreamState, StreamType } from '@/__generated__/graphql.ts';
import { adminAction } from '@/routes/_app/sidebar/menu/menu.css.ts';

const StreamAdminActionsFragment = gql(`
fragment StreamAdminActionsFragment on Stream {
  id
  isFeatured
  streamState
  streamType
}
`);

const SetStreamFeaturedMutation = gql(`
mutation SetStreamFeatured($streamId: ID!, $isFeatured: Boolean!) {
  setStreamFeatured(id: $streamId, isFeatured: $isFeatured) {
    id
    isFeatured
  }
}
`);

const ActivateStreamMutation = gql(`
mutation ActivateStream($streamId: ID!) {
  activateStream(id: $streamId) {
    id
    streamState
  }
}
`);

const DeactivateStreamMutation = gql(`
mutation DeactivateStream($streamId: ID!) {
  deactivateStream(id: $streamId) {
    id
    streamState
  }
}
`);

export const StreamAdminActions = ({
  streamId,
  openHoldModal,
  openAddUsersModal,
  openExportModal,
  openPriceChartModal,
}: {
  streamId: string;
  openHoldModal: () => void;
  openAddUsersModal: () => void;
  openExportModal: () => void;
  openPriceChartModal: () => void;
}) => {
  const { data, complete } = useFragment({
    fragment: StreamAdminActionsFragment,
    fragmentName: 'StreamAdminActionsFragment',
    from: {
      __typename: 'Stream',
      id: streamId,
    },
  });

  const toast = useToast();

  const [setStreamFeatured] = useMutation(SetStreamFeaturedMutation, {
    onError(error) {
      toast.error({
        title: 'Failed to update featured status',
        subTitle: error.message,
      });
    },
  });

  const [activateStream] = useMutation(ActivateStreamMutation, {
    onError(error) {
      toast.error({
        title: 'Failed to activate stream',
        subTitle: error.message,
      });
    },
  });

  const [deactivateStream] = useMutation(DeactivateStreamMutation, {
    onError(error) {
      toast.error({
        title: 'Failed to deactivate stream',
        subTitle: error.message,
      });
    },
  });

  if (!complete) {
    return null;
  }

  const { isFeatured, streamState, streamType } = data;

  return (
    <Fragment>
      <DropdownMenuItem
        className={adminAction}
        onSelect={() => {
          const newValue = !isFeatured;

          setStreamFeatured({
            variables: {
              streamId,
              isFeatured: newValue,
            },
            optimisticResponse: {
              setStreamFeatured: {
                __typename: 'Stream',
                id: streamId,
                isFeatured: newValue,
              },
            },
          });
        }}
        icon={isFeatured ? <StarFilledIcon /> : <StarIcon />}
      >
        {isFeatured ? 'Unmark as Featured' : 'Mark as Featured'}
      </DropdownMenuItem>

      {streamState !== StreamState.Active ? (
        <DropdownMenuItem
          className={adminAction}
          onSelect={() => {
            activateStream({
              variables: {
                streamId,
              },
              optimisticResponse: {
                activateStream: {
                  __typename: 'Stream',
                  id: streamId,
                  streamState: StreamState.Active,
                },
              },
            });
          }}
          icon={<CheckIcon />}
        >
          Activate
        </DropdownMenuItem>
      ) : null}

      {streamState !== StreamState.Inactive ? (
        <DropdownMenuItem
          className={adminAction}
          onSelect={() => {
            deactivateStream({
              variables: {
                streamId,
              },
              optimisticResponse: {
                deactivateStream: {
                  __typename: 'Stream',
                  id: streamId,
                  streamState: StreamState.Inactive,
                },
              },
            });
          }}
          icon={<XmarkIcon />}
        >
          Deactivate
        </DropdownMenuItem>
      ) : null}

      {streamState !== StreamState.OnHold &&
      streamType !== StreamType.Private ? (
        <ModalTrigger key="hold" asChild>
          <DropdownMenuItem
            className={adminAction}
            onSelect={openHoldModal}
            icon={<PauseIcon />}
          >
            Hold
          </DropdownMenuItem>
        </ModalTrigger>
      ) : null}

      <ModalTrigger key="export" asChild>
        <DropdownMenuItem
          className={adminAction}
          onSelect={openExportModal}
          icon={<DownloadDataWindowIcon />}
        >
          Export Posts
        </DropdownMenuItem>
      </ModalTrigger>

      <ModalTrigger key="add-users" asChild>
        <DropdownMenuItem
          className={adminAction}
          onSelect={openAddUsersModal}
          icon={<PlusIcon />}
        >
          Add users
        </DropdownMenuItem>
      </ModalTrigger>

      <ModalTrigger key="price-chart" asChild>
        <DropdownMenuItem
          className={adminAction}
          onSelect={openPriceChartModal}
          icon={<CandlestickChartIcon />}
        >
          View chart
        </DropdownMenuItem>
      </ModalTrigger>

      <DropdownMenuSeparator />
    </Fragment>
  );
};
